import {
  differenceInMinutes,
  differenceInHours,
  isToday,
  isYesterday,
  format,
  isPast,
  differenceInDays,
} from "date-fns";
import { TarefaDTO } from "@/types/tarefa.type";
import { TarefaStatus } from "@workflow/types";
import {
  AlertCircle,
  Briefcase,
  CalendarClock,
  CalendarDays,
  Eye,
  Inbox,
  MessageSquare,
  Send,
} from "lucide-react";
import { cn } from "@/lib/utils";

// --- utils/tarefa-formatters.ts ---

export function formatRelativeTime(dateInput: string | Date | undefined): string {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  const now = new Date();

  const mins = differenceInMinutes(now, date);
  const hours = differenceInHours(now, date);

  if (mins < 1) return "Agora mesmo";
  if (mins < 60) return `Há ${mins} min`;
  if (hours < 24 && isToday(date)) return `Hoje, ${format(date, "HH:mm")}`;
  if (isYesterday(date)) return `Ontem, ${format(date, "HH:mm")}`;

  return format(date, "dd/MM/yyyy 'às' HH:mm");
}

export function identificarPapelUsuario(tarefa: TarefaDTO, userId?: number | string): PapelTarefa {
  if (!userId) return null;
  const id = Number(userId);

  if (Number(tarefa.atribuidoA?.id) === id) return "RESPONSAVEL";
  if (tarefa.tarefaObservadores?.some((obs) => Number(obs.pessoa.id) === id)) return "OBSERVADOR";
  if (Number(tarefa.criadoPor?.id) === id) return "CRIADOR";

  return null;
}

export type StatusPrazo = "ATRASADO" | "VENCENDO" | "NO_PRAZO" | null;
export type PapelTarefa = "RESPONSAVEL" | "OBSERVADOR" | "CRIADOR" | null;

/**
 * Espelha a regra do backend (getAtrasoAgregadoAtribuidas / SituacaoTarefas):
 * tarefa PENDENTE/EM_EXECUCAO cujo atraso — contado do prazo, ou da criação
 * quando não há prazo — chega a 15 dias é "vencida", igual ao aviso do
 * TarefasVencidasDialog. Se mudar o corte lá, mudar aqui.
 * ponytail: constante duplicada do backend (SituacaoTarefas.DIAS_MINIMOS_AVISO);
 * unificar só se virar config compartilhada.
 */
export const DIAS_VENCIDA = 15;

export function isTarefaVencida(tarefa: TarefaDTO): boolean {
  if (tarefa.status !== TarefaStatus.PENDENTE && tarefa.status !== TarefaStatus.EM_EXECUCAO)
    return false;
  const referencia = tarefa.dataPrazo ?? tarefa.criadoEm;
  if (!referencia) return false;
  return differenceInDays(new Date(), new Date(referencia)) >= DIAS_VENCIDA;
}

export function avaliarStatusPrazo(tarefa: TarefaDTO): StatusPrazo {
  if (!tarefa.dataPrazo) return null;
  if (tarefa.status === TarefaStatus.CONCLUIDO || tarefa.status === TarefaStatus.CANCELADO)
    return null;

  const prazo = new Date(tarefa.dataPrazo);
  const hoje = new Date();

  if (isPast(prazo) && !isToday(prazo)) return "ATRASADO";
  if (isToday(prazo) || differenceInDays(prazo, hoje) <= 2) return "VENCENDO";
  return "NO_PRAZO";
}

export const JobBadge = ({ pit }: { pit?: string }) => {
  if (!pit) return null;
  return (
    <span className="flex items-center gap-1 rounded-md border border-border bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
      <Briefcase className="h-2.5 w-2.5" /> PIT {pit}
    </span>
  );
};

export const RoleBadge = ({ papel }: { papel: PapelTarefa }) => {
  const configs = {
    RESPONSAVEL: {
      icon: Inbox,
      text: "Responsável",
      classes: "text-red-600 bg-red-500/10 border-red-500/20",
    },
    OBSERVADOR: {
      icon: Eye,
      text: "Observador",
      classes: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20",
    },
    CRIADOR: {
      icon: Send,
      text: "Solicitante",
      classes: "text-purple-600 bg-purple-500/10 border-purple-500/20",
    },
  };

  if (!papel || !configs[papel]) return null;
  const Config = configs[papel];

  return (
    <span
      className={cn(
        "flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
        Config.classes,
      )}
    >
      <Config.icon className="h-2.5 w-2.5" /> {Config.text}
    </span>
  );
};

/** Mostrado na aba "Mensagens": esta tarefa tem mensagem que essa pessoa ainda não viu. */
export const MensagemNaoLidaBadge = ({ visivel }: { visivel: boolean }) => {
  if (!visivel) return null;

  return (
    <span className="flex items-center gap-1 rounded-md border border-blue-500/20 bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-600">
      <MessageSquare className="h-2.5 w-2.5" /> Nova mensagem
    </span>
  );
};

export const PrazoBadge = ({ status, dataPrazo }: { status: StatusPrazo; dataPrazo?: Date }) => {
  if (!status || !dataPrazo) return null;

  const configs = {
    ATRASADO: {
      icon: AlertCircle,
      label: "Atrasado",
      classes: "text-destructive bg-destructive/10 border-destructive/20",
    },
    VENCENDO: {
      icon: CalendarClock,
      label: "Vencendo",
      classes: "text-amber-600 dark:text-amber-500 bg-amber-500/10 border-amber-500/20",
    },
    NO_PRAZO: {
      icon: CalendarDays,
      label: "Prazo",
      classes: "text-primary bg-primary/10 border-primary/20",
    },
  };

  const Config = configs[status];

  return (
    <span
      className={cn(
        "flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
        Config.classes,
      )}
    >
      <Config.icon className="h-2.5 w-2.5" />
      {Config.label}: {format(new Date(dataPrazo), "dd/MM")}
    </span>
  );
};
